/**
 * Protected route component that handles authentication and auto-logout
 */
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { authService } from '../../utils/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireSuperAdmin = false 
}) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);

      if (authenticated) {
        const user = authService.getCurrentUser();
        // Check if super admin is required and user has permission
        const permission = !requireSuperAdmin || (user?.is_super_admin === true);
        setHasPermission(permission);
      }

      setIsChecking(false);
    };

    checkAuth();

    // Set up periodic auth checks (every 30 seconds)
    const authCheckInterval = setInterval(checkAuth, 30000);

    return () => {
      clearInterval(authCheckInterval);
    };
  }, [requireSuperAdmin]);

  // Show loading spinner while checking authentication
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Show access denied if user doesn't have required permissions
  if (!hasPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md mx-auto text-center">
          <div className="text-red-600 text-4xl mb-4">🚫</div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600 mb-4">
            {requireSuperAdmin 
              ? 'Super admin privileges required to access this page.' 
              : 'You do not have permission to access this page.'
            }
          </p>
          <button
            onClick={() => authService.logout()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};